import {AnimatePresence, motion, useReducedMotion} from "framer-motion";
import {useEffect, useState} from "react";
import {FaPause, FaPlay, FaQuoteLeft} from "react-icons/fa";
import {RevealAnimation} from "../../animations/RevealAnimation";

interface Testimonial {
    id: number;
    content: string;
    author: string;
    role: string;
    context?: string;
    date?: string;
    rotationMs?: number;
}

const TESTIMONIALS: Testimonial[] = [
    {
        id: 1,
        content: "I hired Jarrod to be one of the founding UI engineers to help build our product with ReactJS and TypeScript. Jarrod is an excellent engineer, he possesses the skills to build a beautiful and performant product. He consistently produced quality code that replicated Figma designs and added value to the team by communicating ways we could improve. I was impressed with his immense knowledge in current and emerging technologies, and his ability to problem solve.",
        author: "Amanda Fenn",
        role: "Director of Engineering, REGO Payment Architectures",
    },
    {
        id: 2,
        content: "Working alongside Jarrod has been a fantastic experience! As an engineer with proficiency in React and Figma, he brings a unique blend of technical and design skills to the table. During our project, he showcased a deep understanding of user-centric design principles, seamlessly integrating complex functionalities while ensuring a top-notch user experience.",
        author: "Natalia Yarysheva",
        role: "Software Developer",
    },
    {
        id: 3,
        content: "Really enjoyed working with Jarrod on our mutual software project! Jarrod is hyper competent at React, OOP, testing, and design. He's also a great person to collaborate with and is generous and willing to share his knowledge and expertise with his peers. Great developer to have on any software team!",
        author: "Alexander Da Costa",
        role: "Fullstack Web Developer",
    },
    {
        id: 4,
        content: "Working with Jarrod at Pinnacle was a great highlight during my time there. As someone coming from a coaching and behavioral science background, I always appreciated how quickly Jarrod could take a concept, sometimes even just a rough idea from a meeting, and turn it into something that actually worked beautifully on screen. He has this rare ability to bridge the gap between abstract product thinking and clean, intuitive execution.\n\nWhat I valued most was how thoughtful and collaborative he was throughout the process. He never made you feel like my input was \"too non-technical\" - he genuinely welcomed it. His speed and technical range are impressive, but it's his communication style and positive energy that made cross-functional work feel easy.\n\nAny team building something meaningful would be lucky to have Jarrod. He doesn't just ship features - he helps shape the product and the culture around it.",
        author: "Chloe Mackey, PCC",
        role: "ICF-Certified Professional Coach · Whole-Person Employee Coaching",
        context: "Worked with Jarrod on the same team at Pinnacle",
        date: "2026-03-10",
        rotationMs: 60000,
    },
];

const ROTATION_MS = 12000;

export const TestimonialsSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const prefersReducedMotion = useReducedMotion();
    // Auto-rotating content needs a pause control (WCAG 2.2.2), and reduced-motion
    // users shouldn't get movement they didn't ask for.
    const [isPlaying, setIsPlaying] = useState(!prefersReducedMotion);

    useEffect(() => {
        if (!isPlaying) return;
        const timer = setTimeout(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % TESTIMONIALS.length);
        }, TESTIMONIALS[currentIndex].rotationMs ?? ROTATION_MS);
        return () => clearTimeout(timer);
    }, [currentIndex, isPlaying]);

    const active = TESTIMONIALS[currentIndex];

    return (
        <section
            id="testimonials"
            className="section shell">
            <RevealAnimation>
                <h2 className="section-title">What Colleagues Say</h2>

                <div className="relative bg-slate-800 rounded-xl p-8 md:p-12 overflow-hidden">
                    <div
                        className="absolute top-0 left-0 opacity-5 text-9xl"
                        aria-hidden="true">
                        <FaQuoteLeft />
                    </div>

                    <div className="relative min-h-[350px] flex flex-col justify-between">
                        <div aria-live="polite">
                            {/* mode="wait" can leave the outgoing slide stuck forever if its exit
                                animation's completion callback never fires (seen with the Web
                                Animations API in some browsers); mounting the incoming slide
                                immediately avoids depending on that callback. */}
                            <AnimatePresence initial={false}>
                                <motion.figure
                                    key={currentIndex}
                                    initial={{opacity: 0, y: 20}}
                                    animate={{opacity: 1, y: 0}}
                                    exit={{opacity: 0, y: -20}}
                                    transition={{duration: 0.5}}
                                    className="mb-8">
                                    <blockquote className="text-slate-300 text-lg italic mb-8 leading-relaxed whitespace-pre-line">{active.content}</blockquote>
                                    <figcaption>
                                        <span className="block text-blue-300 font-semibold text-xl">{active.author}</span>
                                        <span className="block text-slate-400">{active.role}</span>
                                        {active.context && <span className="block text-slate-400 text-sm mt-2">{active.context}</span>}
                                        {active.date && <time className="block text-slate-400 text-sm mt-1" dateTime={active.date}>{new Date(`${active.date}T12:00:00`).toLocaleDateString("en-US", {month: "long", day: "numeric", year: "numeric"})}</time>}
                                    </figcaption>
                                </motion.figure>
                            </AnimatePresence>
                        </div>

                        <div className="flex items-center justify-center gap-4 mt-6">
                            <button
                                onClick={() => setIsPlaying((playing) => !playing)}
                                aria-label={isPlaying ? "Pause testimonial rotation" : "Resume testimonial rotation"}
                                className="p-2 rounded-full bg-slate-700 text-blue-200 hover:bg-slate-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800">
                                {isPlaying ? <FaPause size={12} /> : <FaPlay size={12} />}
                            </button>

                            <div className="flex gap-3">
                                {TESTIMONIALS.map((testimonial, index) => (
                                    <button
                                        key={testimonial.id}
                                        onClick={() => {
                                            setCurrentIndex(index);
                                            setIsPlaying(false);
                                        }}
                                        aria-label={`Show testimonial from ${testimonial.author}`}
                                        aria-current={index === currentIndex}
                                        className={`w-3 h-3 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800 ${index === currentIndex ? "bg-blue-500 scale-125" : "bg-slate-600 hover:bg-slate-500"}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </RevealAnimation>
        </section>
    );
};
